import React, { Component, Suspense } from 'react';
import { autobind } from 'core-decorators';
import dateFormat from 'dateformat';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import request from '@host-panel/request';

import { ALL as allDevices } from '../../../models/device-type';
import { ALL as allSources } from '../../../models/source-types';
import {
  fetchMonetizationData,
  fetchMonetizationErrorMessage
} from '../../../../analytics/monetization/monetizationActions';
import {
  MonetizationDataItem,
  MonetizationSummary,
  PageInfo,
  SortDirs
} from '../../../../analytics/monetization/monetizationTypes';
import { setSortDirs } from '../../../../analytics/monetization/monetizationSlice';

import Summary from './summary';
import Header from './header';
import download from './icons/download';

import addComma from 'utils/add-comma';
import { AnalyticsProvider } from 'app/scripts/services/analytics-provider';
import { State } from 'state';

const toFixed = num => {
  if (!num) {
    num = Number(0);
  }
  return num.toFixed(2);
};

const MonetizationTable = React.lazy(
  () => import(/* webpackChunkName: 'monetization-table' */ './MonetizationTable')
);

interface MonetizationProps {
  spotId: string;
  summary: MonetizationSummary;
  data: Array<MonetizationDataItem>;
  pageInfo: PageInfo;
  sortDirs: SortDirs;
  errorMessage: string;
  trackEventName: string;
  fetchMonetization: (spotId: string, pageInfo?: PageInfo) => void;
  fetchErrorMessage: (spotId: string) => void;
  setSortDirs: (sortDirs: SortDirs) => void;
}

class Monetization extends Component<MonetizationProps> {
  static displayName = 'Monetization';

  @autobind
  onSortChange(columnKey) {
    const { sortDirs } = this.props;

    const newSortDirs: SortDirs = { [columnKey]: sortDirs[columnKey] === 'DESC' ? 'ASC' : 'DESC' };
    this.props.setSortDirs(newSortDirs);
  }

  @autobind
  componentDidMount() {
    const { spotId, trackEventName } = this.props;

    this.props.fetchMonetization(spotId);
    this.props.fetchErrorMessage(spotId);

    const element = document.querySelector('.fixedDataTableLayout_main');
    if (element) {
      let height = element.style.height;
      height = parseInt(height.substring(0, height.length - 2)) - 16;
      element.style.height = `${height}px`;
    }

    if (trackEventName) {
      AnalyticsProvider.track(trackEventName);
    }
  }

  @autobind
  handleImpressionsClick() {
    this.refs.impressions.toggle();
  }

  @autobind
  onFiltersChange(newFilters) {
    const { spotId, pageInfo } = this.props;

    this.props.fetchMonetization(spotId, { ...pageInfo, ...newFilters });
  }

  @autobind
  async exportCsv() {
    const { spotId, pageInfo } = this.props;
    const { fromDay, toDay, device, source } = pageInfo;
    const data = {
      spot_id: spotId,
      from: fromDay,
      to: toDay,
      format: 'csv',
      ...(device === allDevices ? {} : { device }),
      ...(source === allSources ? {} : { source })
    };

    try {
      const blob = await request('/host-panel/monetization/data', {
        data,
        isFetchingFile: true
      });
      const a = document.createElement('a');

      a.href = window.URL.createObjectURL(blob);
      a.download = 'monetization_data.csv';
      a.click();
    } catch (err) {
      console.error(err);
    }
  }

  render() {
    const { pageInfo, summary, sortDirs, data, errorMessage, spotId } = this.props;

    const items = [
      {
        amount: `$${addComma(toFixed(summary.publisherRevenue))}`,
        description: 'Revenues (total)'
      },
      {
        amount: `$${addComma(toFixed(summary.erpm))}`,
        description: 'eRPM (average)'
      },
      {
        amount: `${addComma(toFixed(summary.fillRate))}%`,
        description: 'Fill Rate (average)'
      }
    ];

    return (
      <div className="analytics">
        <Header
          headerText="Monetization"
          pageInfo={pageInfo}
          onFiltersChange={this.onFiltersChange}
          spotId={spotId}
        />
        <Summary items={items} />
        <div className="table-view" ref="handler">
          {errorMessage ? <div className="error_msg">{errorMessage}</div> : null}
          <div className="title-container">
            <div className="table-title">Daily Revenues</div>
            <div className="download-button" onClick={this.exportCsv}>
              {download}
            </div>
          </div>
          <Suspense fallback={null}>
            <MonetizationTable data={data} sortDirs={sortDirs} onSortChange={this.onSortChange} />
          </Suspense>
        </div>
        <div className="disclaimer">
          <b>Disclaimer</b>: All revenues are operational and should not be treated as final.
        </div>
      </div>
    );
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    fetchMonetization: (spotId: string, pageInfo?: PageInfo) => {
      let info: PageInfo = undefined;

      if (pageInfo) {
        info = pageInfo;
      } else {
        const now = new Date();
        const toDay = dateFormat(now, 'yyyy-mm-dd');
        const fromDay = dateFormat(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-mm-dd');

        info = {
          device: 'all',
          source: 'all',
          fromDay,
          toDay,
          period: 'THIS_MONTH'
        };
      }

      dispatch(
        fetchMonetizationData({
          spotId,
          ...info
        })
      );
    },
    fetchErrorMessage: (spotId: string) => dispatch(fetchMonetizationErrorMessage(spotId)),
    setSortDirs: (sortDirs: SortDirs) => dispatch(setSortDirs(sortDirs))
  };
}

function mapStateToProps(state: State) {
  const { summary, data, sortDirs, pageInfo, errorMessage } = state.analytics.monetization;

  return {
    summary,
    data,
    pageInfo,
    sortDirs,
    errorMessage
  };
}

const ConnectedMonetization = connect(mapStateToProps, mapDispatchToProps)(Monetization);

export default ConnectedMonetization;
